import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@repo/api-client";

// ── Inventory data from AekamPass site ─────────────────────────────
interface PosItem {
  id: string;
  name: string;
  price: number;
  category: string;
  emoji: string;
  qty: number;
}

const INVENTORY: PosItem[] = [
  // Pyramids
  { id: "PYR-HLT", name: "Health Pyramid", price: 25, category: "Pyramids", emoji: "🔺", qty: 20 },
  { id: "PYR-WLT", name: "Wealth Pyramid", price: 25, category: "Pyramids", emoji: "🔺", qty: 20 },
  { id: "PYR-PRT", name: "Protection Pyramid", price: 25, category: "Pyramids", emoji: "🔺", qty: 20 },
  { id: "PYR-REL", name: "Relations Pyramid", price: 25, category: "Pyramids", emoji: "🔺", qty: 20 },
  { id: "PYR-YAN", name: "Yantra Pyramid", price: 50, category: "Pyramids", emoji: "🔺", qty: 10 },
  // Siva Lingam
  { id: "SIV-SPA-MED", name: "Spatika Lingam (Medium)", price: 15, category: "Siva Lingam", emoji: "🪨", qty: 15 },
  { id: "SIV-SPA-SM", name: "Spatika Lingam (Small)", price: 9, category: "Siva Lingam", emoji: "🪨", qty: 15 },
  { id: "SIV-MAR", name: "Markata Lingam", price: 25, category: "Siva Lingam", emoji: "🪨", qty: 10 },
  // Other
  { id: "OTH-CRY-BTL", name: "Crystal Bottle", price: 30, category: "Other", emoji: "🫙", qty: 12 },
  { id: "OTH-EE-L", name: "Evil Eye (Large)", price: 15, category: "Other", emoji: "🧿", qty: 25 },
  { id: "OTH-EE-M", name: "Evil Eye (Medium)", price: 7, category: "Other", emoji: "🧿", qty: 25 },
  { id: "OTH-EE-S", name: "Evil Eye (Small)", price: 4, category: "Other", emoji: "🧿", qty: 30 },
  { id: "OTH-EE-XS", name: "Evil Eye (Mini)", price: 3, category: "Other", emoji: "🧿", qty: 30 },
  { id: "OTH-HANG-L", name: "Hanging (Large)", price: 11, category: "Other", emoji: "🪬", qty: 15 },
  { id: "OTH-HANG-S", name: "Hanging (Small)", price: 9, category: "Other", emoji: "🪬", qty: 15 },
  { id: "OTH-PYR-TRT", name: "Pyrite Turtle", price: 11, category: "Other", emoji: "🐢", qty: 10 },
  { id: "OTH-TORT", name: "Tortoise", price: 5, category: "Other", emoji: "🐢", qty: 15 },
  { id: "OTH-RQ-SWN", name: "Rose Quartz Swan", price: 25, category: "Other", emoji: "🦢", qty: 8 },
  { id: "OTH-PYR-KEY", name: "Pyrite Keychain", price: 5, category: "Other", emoji: "🔑", qty: 20 },
  { id: "OTH-SEL", name: "Selenite", price: 20, category: "Other", emoji: "✨", qty: 12 },
  { id: "OTH-EYE-MSK", name: "Eye Masks", price: 5, category: "Other", emoji: "😴", qty: 20 },
  { id: "OTH-LCK-S", name: "Locket (Small)", price: 5, category: "Other", emoji: "📿", qty: 15 },
  { id: "OTH-LCK-L", name: "Locket (Large)", price: 9, category: "Other", emoji: "📿", qty: 12 },
  // Bracelets
  { id: "BRC-MMG", name: "Money Magnet Bracelet", price: 11, category: "Bracelets", emoji: "💚", qty: 20 },
  { id: "BRC-3PT", name: "Triple Protection Bracelet", price: 11, category: "Bracelets", emoji: "💙", qty: 20 },
  { id: "BRC-MIX", name: "Mix Bracelet", price: 18, category: "Bracelets", emoji: "💛", qty: 15 },
  { id: "BRC-MCL", name: "Mix Colour Bracelet", price: 15, category: "Bracelets", emoji: "🌈", qty: 20 },
];

const CATEGORIES = ["All", "Pyramids", "Siva Lingam", "Other", "Bracelets"];
const PAYMENT_METHODS = ["Cash", "Card", "Zelle", "Venmo", "Other"] as const;
type PaymentMethodLabel = typeof PAYMENT_METHODS[number];
const PM_MAP: Record<PaymentMethodLabel, string> = { Cash: "cash", Card: "card", Zelle: "zelle", Venmo: "venmo", Other: "other" };

interface CartLine {
  item: PosItem;
  qty: number;
}

export function PosPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [cart, setCart] = useState<CartLine[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodLabel>("Cash");
  const [orderComplete, setOrderComplete] = useState(false);
  const [lastOrderTotal, setLastOrderTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [inventory, setInventory] = useState<PosItem[]>(INVENTORY);

  useEffect(() => {
    api.listPosItems().then((res) => {
      if (res.items && res.items.length > 0) {
        setInventory(res.items.map((i) => ({
          id: i.id,
          name: i.name,
          price: i.price,
          category: i.category,
          emoji: "📦",
          qty: i.inventory_count ?? 99,
        })));
      }
    }).catch(() => {});
  }, []);

  const filteredItems = inventory.filter((item) => {
    const matchCat = activeCategory === "All" || item.category === activeCategory;
    const matchSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  function addToCart(item: PosItem) {
    setCart((prev) => {
      const existing = prev.find((l) => l.item.id === item.id);
      if (existing) {
        return prev.map((l) =>
          l.item.id === item.id ? { ...l, qty: l.qty + 1 } : l
        );
      }
      return [...prev, { item, qty: 1 }];
    });
  }

  function updateQty(id: string, delta: number) {
    setCart((prev) =>
      prev
        .map((l) => (l.item.id === id ? { ...l, qty: l.qty + delta } : l))
        .filter((l) => l.qty > 0)
    );
  }

  const subtotal = cart.reduce((sum, l) => sum + l.item.price * l.qty, 0);

  async function handleCharge() {
    const lines = cart.map((l) => ({ item_id: l.item.id, item_name: l.item.name, price: l.item.price, qty: l.qty }));
    const pm = PM_MAP[paymentMethod] as "cash" | "card" | "zelle" | "venmo" | "other";
    try {
      await api.createPosOrder({ lines, payment_method: pm, delivery_fee: 0 });
    } catch (_) { /* best-effort — still show success UI */ }
    setLastOrderTotal(subtotal);
    setCart([]);
    setOrderComplete(true);
    setTimeout(() => setOrderComplete(false), 5000);
  }

  return (
    <div className="pos-page">
      <div className="container container--wide">
        {/* Header */}
        <div className="pos-header">
          <div>
            <span className="eyebrow">AekamPass · AekamMart POS</span>
            <h1 className="display-3">Sacred Items Shop</h1>
          </div>
          <Link to="/ops" className="btn btn--outline btn--sm">← Back to Ops</Link>
        </div>

        {/* Order complete banner */}
        {orderComplete && (
          <div className="checkin-success-banner" style={{ marginBottom: "1.5rem" }}>
            <span className="checkin-success-banner__icon">✓</span>
            <div>
              Order complete! &nbsp;<strong>${lastOrderTotal.toFixed(2)}</strong> charged via {paymentMethod}.
            </div>
          </div>
        )}

        <div className="pos-layout">
          {/* Left: Catalog */}
          <div className="pos-catalog">
            {/* Search */}
            <div style={{ marginBottom: "1rem" }}>
              <input
                type="search"
                className="checkin-search-input"
                placeholder="Search items…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Category tabs */}
            <div className="pos-category-tabs">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={`pos-cat-tab${activeCategory === cat ? " active" : ""}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Item grid */}
            <div className="pos-item-grid">
              {filteredItems.map((item) => {
                const inCart = cart.find((l) => l.item.id === item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`pos-item-card${inCart ? " pos-item-card--in-cart" : ""}`}
                    onClick={() => addToCart(item)}
                  >
                    <span className="pos-item-card__emoji">{item.emoji}</span>
                    <div className="pos-item-card__name">{item.name}</div>
                    <div className="pos-item-card__price">${item.price}</div>
                    {inCart && (
                      <span className="pos-item-card__badge">×{inCart.qty}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: Cart + checkout */}
          <div className="pos-cart-panel">
            <div className="pos-cart-header">
              🛒 Cart {cart.length > 0 && `(${cart.reduce((s, l) => s + l.qty, 0)} items)`}
            </div>

            {cart.length === 0 ? (
              <div className="pos-cart-empty">Tap items to add them to the cart</div>
            ) : (
              <>
                <div className="pos-cart-lines">
                  {cart.map((line) => (
                    <div key={line.item.id} className="pos-cart-line">
                      <div className="pos-cart-line__name">
                        {line.item.emoji} {line.item.name}
                      </div>
                      <div className="pos-cart-line__qty">
                        <button
                          type="button"
                          className="pos-qty-btn"
                          onClick={() => updateQty(line.item.id, -1)}
                        >−</button>
                        <span>{line.qty}</span>
                        <button
                          type="button"
                          className="pos-qty-btn"
                          onClick={() => updateQty(line.item.id, 1)}
                        >+</button>
                      </div>
                      <div className="pos-cart-line__total">
                        ${(line.item.price * line.qty).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pos-cart-subtotal">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>

                {/* Payment method */}
                <div className="pos-payment-methods">
                  {PAYMENT_METHODS.map((method) => (
                    <button
                      key={method}
                      type="button"
                      className={`pos-payment-btn${paymentMethod === method ? " active" : ""}`}
                      onClick={() => setPaymentMethod(method)}
                    >
                      {method}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="btn btn--brand"
                  style={{ width: "100%", marginTop: "1rem", padding: "1rem" }}
                  onClick={() => void handleCharge()}
                >
                  Charge ${subtotal.toFixed(2)} · {paymentMethod}
                </button>

                <button
                  type="button"
                  className="btn btn--ghost"
                  style={{ width: "100%", marginTop: "0.5rem" }}
                  onClick={() => setCart([])}
                >
                  Clear Cart
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
